import ValidateModule from "./ValidateModule";

import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { IEventChannel } from "../model/Models";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

export default class AnalysisModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });
    }

    /**
     * Get source layerViews ids (i.e. the layerviews with the original data used as the analysis input)
     * 
     * @param analysis 
     * 
     * @returns source layerviews ids
     */
    getSourceLayerViewIds(analysis): number[] {
        if (!ValidateModule.checkMandatoryArg(analysis)) return [];
        
        if (analysis.$type == "AggregateByAttributeAnalysis") {
            return [analysis.meta.layerViewId];
        }
        
        return [analysis.meta.drilldownLayerViewId, analysis.meta.aggregateLayerViewId];
    }
    
    /**
     * Get generated layerViews ids (i.e. the layerviews with the result data a.k.a. analysis output)
     * 
     * @param analysis
     * 
     * @returns generated layerviews ids
     */
    getGeneratedLayerViewIds(analysis): number[] {
        if (!ValidateModule.checkMandatoryArg(analysis)) return [];
        
        return [analysis.generatedDrilldownLayerViewId, analysis.generatedAggregateLayerViewId];
    }
    
    /**
     * Gets default meta
     * 
     * @param type 
     * @param aggregateLayerViewId 
     * @param drilldownLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    getDefaultAggregateMeta(type: string, aggregateLayerViewId: number, drilldownLayerViewId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(type) ||
                !ValidateModule.checkId(aggregateLayerViewId) ||
                !ValidateModule.checkId(drilldownLayerViewId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/defaultMeta/' + type
            + '?aggregateLayerViewId=' + aggregateLayerViewId
            + '&drilldownLayerViewId=' + drilldownLayerViewId;

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        });
    }
    
    /**
     * Gets an analysis
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/' + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }

    /**
     * Get analysis by geo map id.
     *
     * @param geoMapId id of the geomap to get analysis object of
     * @param opts
     * 
     * @returns
     */
    getByGeoMapId(geoMapId: number, opts?: ApiOpts) {        
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/geomap/' + geoMapId;
        return this.cache.doGet(this.rqBroker, this.resourceName + "_byGeoMapId", url, geoMapId, opts);
    }

    /**
     * Get analysis ids by geoMap ids.
     *
     * @param geoMapIds list of the geomap ids to get analysis ids
     * @param opts
     * 
     * @returns map of geoMap id to analysis id relations, maps without analysis are excluded
     */
    idsByGeoMapIds(geoMapIds: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(geoMapIds)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/geomap/ids?ids=' + es.util.url.stringify(geoMapIds);
        return this.cache.doGet(this.rqBroker, this.resourceName + "_idsByGeoMapIds", url, geoMapIds, opts);
    }
    
    /**
     * Creates an analysis
     * 
     * @param model
     * @param opts
     * 
     * @returns
     */
    analyse(model, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(model)) {
            return es.util.deferred.rejected();
        }

        this.cache.clear('AsyncTasks');
        es.api.getEventChannel().fireEvent("onBeforeUpdateGeomap"); // Status module might update geomap
        
        return this.rqBroker.doPost({
            'url': es.api.getURL(this.resourceName),
            'opts': opts,
            'data': model
        });
    }
    
    /**
     * Updates an existing analysis
     * 
     * @param id
     * @param model
     * @param opts
     * 
     * @returns 
     */
    update(id: number, model, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id) ||
            !ValidateModule.checkMandatoryArg(model)) {
            return es.util.deferred.rejected();
        }
        
        this.cache.clear('AsyncTasks');
        es.api.getEventChannel().fireEvent("onBeforeUpdateGeomap"); // Status module might update geomap
        
        return this.rqBroker.doPut({
            'url': es.api.getURL(this.resourceName) + '/' + id,
            opts: opts,
            'data': model
        });
    }
    
    revert(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        // we need access to the header so we can see what the associated geomap id is
        opts = $.extend(undefined, {resolveWithXHR: true});
        const url = es.api.getURL(this.resourceName) + '/' + id;

        return this.rqBroker.doDelete({url: url, opts: opts}).then((rs, jqXHR) => {
            // check this
            var geoMapId = parseInt(jqXHR.getResponseHeader("es_geoMap_id"));
            this.cache.clear(this.resourceName, id);
            this.cache.clear(this.resourceName + "_byGeoMapId");
            this.cache.clear(this.resourceName + "_idsByGeoMapIds");
            this.cache.clear("GeoMap", geoMapId);

            return es.api.appResources.setWorkingCopyChanged(geoMapId).then(() => {
                this.eventChannel?.fireEvent("onRemoveRoute", rs);
                
                return rs;
            });
        });
    }
}
